import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { fmtMonth } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#059669", "#0284c7", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#64748b", "#ca8a04"];

export default function MyStats() {
  const { user } = useAuth();
  const { query } = usePlanningPeriod();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    api.get(`/statistics/volunteer/${user.id}?${query}`)
      .then((r) => { if (active) setStats(r.data); })
      .catch(() => { if (active) setStats(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, user]);

  const typeData = stats ? Object.entries(stats.by_type).map(([k, v]) => ({ name: k, value: v })) : [];
  const monthData = stats ? stats.by_month.map((m) => ({ name: fmtMonth(m.month), diensten: m.count })) : [];
  const dev = stats?.deviation_pct || 0;
  const DevIcon = dev > 5 ? TrendingUp : dev < -5 ? TrendingDown : Minus;
  const devColor = dev > 5 ? "text-amber-600" : dev < -5 ? "text-sky-600" : "text-red-600";

  return (
    <div className="space-y-6" data-testid="my-stats-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Mijn statistieken</h1>
          <p className="text-muted-foreground mt-1">Jouw gepubliceerde diensten en verdeling binnen de gekozen periode.</p>
        </div>
        <PlanningPeriodSelector />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : !stats ? (
        <Card className="p-10 text-center text-muted-foreground">Geen gegevens.</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5" data-testid="stat-total"><p className="text-3xl font-bold font-heading">{stats.total}</p><p className="text-xs text-muted-foreground mt-1">Totaal diensten</p></Card>
            <Card className="p-5"><p className="text-3xl font-bold font-heading">{stats.availability.available + stats.availability.preferred}</p><p className="text-xs text-muted-foreground mt-1">Beschikbaar / voorkeur</p></Card>
            <Card className="p-5"><p className="text-3xl font-bold font-heading">{stats.swaps}</p><p className="text-xs text-muted-foreground mt-1">Ruilverzoeken</p></Card>
            <Card className="p-5">
              <div className={`flex items-center gap-1.5 ${devColor}`}>
                <DevIcon className="w-6 h-6" />
                <p className="text-3xl font-bold font-heading">{dev > 0 ? "+" : ""}{dev}%</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">t.o.v. gemiddelde ({stats.group_average})</p>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4">Diensten per maand</h3>
              {monthData.length === 0 ? <p className="text-sm text-muted-foreground">Geen data</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={monthData}>
                    <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip />
                    <Bar dataKey="diensten" fill="#059669" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4">Verdeling werkzaamheden</h3>
              {typeData.length === 0 ? <p className="text-sm text-muted-foreground">Geen data</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {typeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
